import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DefaultToDateRouteParam } from '@dashboard/dashboard-store/reducers/ui-state.initial';

const RETURN_URL_KEY = 'returnUrl';

/**
 * Service to manage return URL for redirecting users after login.
 *
 * Use cases:
 * 1. User opens app from URL while not logged in → redirects to login → after login, redirects back
 * 2. Session expires → redirects to login → after login, redirects back to where they were
 * 3. User explicitly logs out → does NOT redirect back to last URL
 */
@Injectable({
  providedIn: 'root',
})
export class ReturnUrlService {
  constructor(private router: Router) {}

  /**
   * Save the current URL to redirect back after login.
   * Should be called before redirecting to login page.
   */
  saveReturnUrl(url?: string): void {
    const urlToSave = url || this.router.url;

    // Don't save auth-related URLs as return URLs
    if (this.isAuthUrl(urlToSave)) {
      return;
    }

    sessionStorage.setItem(RETURN_URL_KEY, urlToSave);
  }

  /**
   * Get the saved return URL.
   * Returns null if no URL is saved.
   */
  getReturnUrl(): string | null {
    return sessionStorage.getItem(RETURN_URL_KEY);
  }

  /**
   * Clear the saved return URL.
   * Should be called after successful redirect or on logout.
   */
  clearReturnUrl(): void {
    sessionStorage.removeItem(RETURN_URL_KEY);
  }

  /**
   * Redirect to the saved return URL or default path.
   * Clears the return URL after redirect.
   *
   * @param defaultPath - Path to redirect to if no return URL is saved (default: '')
   */
  redirectToReturnUrl(defaultPath: string = ''): void {
    const returnUrl = this.getReturnUrl();
    this.clearReturnUrl();

    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
    } else {
      this.router.navigate(['/dashboard'], {
        queryParams: { to: DefaultToDateRouteParam() },
      });
    }
  }

  /**
   * Check if a URL is an authentication-related URL.
   * We don't want to redirect back to login/auth pages.
   */
  private isAuthUrl(url: string): boolean {
    return url.startsWith('/auth') || url === '/';
  }
}
